import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { LEAD_FORM } from '@/constants/testIds';
import { submitLead } from '@/lib/api';

const interests = [
  'Platform partnership',
  'Data / Analytics',
  'OEM solutions',
  'Dealership solutions',
  'Insurance / Lending',
  'Investment / Venture',
  'Press / Other',
];

const initial = {
  name: '',
  email: '',
  company: '',
  role: '',
  interest: interests[0],
  message: '',
};

export const LeadForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      setForm(initial);
      toast.success('Message received. We will be in touch shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Could not submit — please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={LEAD_FORM.success}
        className="border border-amber/40 bg-amber/5 p-10"
      >
        <div className="overline text-amber mb-3">Message received</div>
        <h3 className="font-display text-2xl md:text-3xl mb-3">
          Thanks for reaching out.
        </h3>
        <p className="text-zinc-300 text-sm leading-relaxed mb-6">
          A member of The Mobility Group will respond within two business days.
          For urgent matters, mention it in your message and we will fast-track.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors inline-flex items-center gap-2"
        >
          Send another <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={LEAD_FORM.root}
      className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10"
    >
      <Field label="Name" required>
        <input
          required
          type="text"
          value={form.name}
          onChange={update('name')}
          data-testid={LEAD_FORM.name}
          placeholder="Your full name"
          className={inputCls}
        />
      </Field>
      <Field label="Email" required>
        <input
          required
          type="email"
          value={form.email}
          onChange={update('email')}
          data-testid={LEAD_FORM.email}
          placeholder="you@company.com"
          className={inputCls}
        />
      </Field>
      <Field label="Company">
        <input
          type="text"
          value={form.company}
          onChange={update('company')}
          data-testid={LEAD_FORM.company}
          placeholder="Organization"
          className={inputCls}
        />
      </Field>
      <Field label="Role">
        <input
          type="text"
          value={form.role}
          onChange={update('role')}
          data-testid={LEAD_FORM.role}
          placeholder="Your role / title"
          className={inputCls}
        />
      </Field>
      <Field label="Interest" full>
        <select
          value={form.interest}
          onChange={update('interest')}
          data-testid={LEAD_FORM.interest}
          className={`${inputCls} appearance-none`}
        >
          {interests.map((opt) => (
            <option key={opt} value={opt} className="bg-ink-900 text-white">
              {opt}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Message" required full>
        <textarea
          required
          value={form.message}
          onChange={update('message')}
          data-testid={LEAD_FORM.message}
          placeholder="Tell us briefly what you're working on or exploring..."
          rows={5}
          className={`${inputCls} resize-y min-h-[120px]`}
        />
      </Field>
      <div className="md:col-span-2 bg-ink-900 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 leading-relaxed">
          We respond within 2 business days
          <br />
          <span className="text-zinc-400">
            or email us at{' '}
            <a
              href="mailto:sales@themobilitygroup.co"
              data-testid="lead-form-email-link"
              className="text-amber hover:underline normal-case tracking-normal"
            >
              sales@themobilitygroup.co
            </a>
          </span>
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid={LEAD_FORM.submit}
          className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending
            </>
          ) : (
            <>
              Send Message
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const inputCls =
  'w-full bg-ink-900 text-white placeholder:text-zinc-600 px-4 py-3 border-0 outline-none focus:bg-black focus:ring-1 focus:ring-amber focus:ring-inset font-sans text-sm';

const Field = ({ label, children, required, full }) => (
  <label className={`block bg-ink-900 ${full ? 'md:col-span-2' : ''}`}>
    <div className="px-4 pt-4 pb-1 flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </span>
      {required && (
        <span className="font-mono text-[10px] text-amber">required</span>
      )}
    </div>
    {children}
  </label>
);

export default LeadForm;
